import _ from "lodash";
import React from "react";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";

import UIBaseComponent from "UIComponents/UIBaseComponent";
import I18nHOC from "UIComponents/I18nHOC";
import FieldComponent from "UnitPlans/components/IBcomponents/FieldComponent";
import LabelComponent from "UnitPlans/components/LabelComponent";
import { fontStyles } from "@toddle-edu/ds-theme";
import PypEvaluationV2 from "AssessmentEvaluation/components/PypEvaluationV2";
import AssessmentLearningGoals from "AppComponents/AssessmentLearningGoals";
import { getIsLearningGoalsRevampEnabled } from "Worksheet/modules/WorksheetFeatureFlags";

import {
  updateAssessmentInRedux,
  updateAssessment,
} from "Assessments/modules/AssessmentModule";
import {
  getElementValue,
  checkEmptyElementValue,
} from "modules/ElementValueUtils";
import { getSubFields } from "UnitPlans/components/LibraryCommonComponents/ResourceEdit/modules/Utils";
import {
  getUserType,
  getCurrentCurriculumProgramType,
} from "modules/CommonSelectors";

import LearningGoalsModal from "./components/LearningGoalsModal";
import { ASSESSMENT } from "Constants/stringConstants";

const styles = {
  labelStyle: {
    fontSize: "1.4rem",
    lineHeight: "2rem",
    ...fontStyles.demiBold,
  },
  evaluationContainerStyle: {
    marginBottom: "16px",
  },
  evaluationBodyStyle: {
    padding: "0px",
    borderRadius: "8px",
  },
  criteriaSetHeaderRowStyle: {
    backgroundColor: "var(--violet-950)",
    padding: "0px 12px",
    height: "44px",
  },
  leftContainerStyle: {
    left: "12px",
  },
  headerRowLeftContainerStyle: {
    backgroundColor: "var(--violet-950)",
  },
  customHeaderLabelStyle: {
    fontSize: "1.4rem",
    ...fontStyles.demiBold,
    color: "var(--text-primary)",
  },
  criteriaSetHeaderLabelPreviewStyle: {
    fontSize: "1.4rem",
    ...fontStyles.demiBold,
    color: "var(--text-primary)",
  },
  headerIconStyle: {
    color: "var(--icon-secondary)",
  },
  ratingContainer: {
    marginTop: "8px",
  },
};

class AddLearningGoals extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      isModalVisible: false,
      selectedFieldKey: null,
    };

    this.learningGoalsModalRef = React.createRef(null);
  }

  onCloseLearningGoalsModal = () => {
    this.setState({ isModalVisible: false, selectedFieldKey: null }, () => {
      this.props.onClose?.();
    });
  };

  onEditClick = key => this.onDropdownItemClick({ key });

  onDropdownItemClick = ({ key }) => {
    this.setState({
      isModalVisible: true,
      selectedFieldKey: key,
    });
  };

  renderLearningGoalsModal = () => {
    const { selectedFieldKey } = this.state;

    const {
      unitPlanFields,
      assessmentFields,
      updateInputField,
      selectedCourseId,
      assessmentType,
      unitPlanFieldList,
      skipQuery,
      value,
      shouldConsiderFieldValue = false,
      templateId,
      assessmentTools,
      testId,
      getLearningGoalsTagCountTooltip,
      field_list,
      options,
      learningGoalsFields,
      curriculumProgram,
      learningCourseId,
    } = this.props;

    const fieldList = field_list || learningGoalsFields || [];
    const fieldObj = _.get(fieldList, selectedFieldKey, {});

    const { config = {}, subFields = [], dataField, type } = fieldObj;

    const {
      type: fieldType,
      options: fieldObjConfigOptions = [],
      plannerElementConfig = {},
      viewLabel,
      viewLabelLocale,
      emptyPopupSubText,
      hasAddRemoveValues = false,
      showSearchFilter,
      showSideNavigation,
      showRadioButton,
      showPopupForSubFields,
    } = config || {};

    const { type: plannerElementType, subType: plannerElementSubType } =
      plannerElementConfig || {};
    const isPlannerElement = _.includes(
      ["PLANNER_ELEMENT", "PYP_PLANNER_ELEMENT"],
      fieldType
    );
    const fieldLabel = _.get(
      _.find(options, { key: selectedFieldKey }),
      "label",
      ""
    );

    return (
      <LearningGoalsModal
        type={type}
        customRef={this.learningGoalsModalRef}
        fieldKey={selectedFieldKey}
        fieldLabel={fieldLabel}
        plannerElementType={plannerElementType}
        unitPlanFields={unitPlanFields}
        assessmentFields={assessmentFields}
        updateInputField={updateInputField}
        onCloseLearningGoalsModal={this.onCloseLearningGoalsModal}
        isPlannerElement={isPlannerElement}
        subFields={subFields}
        selectedCourseId={selectedCourseId}
        assessmentType={assessmentType}
        unitPlanFieldList={unitPlanFieldList}
        dataField={dataField}
        emptyPopupSubText={emptyPopupSubText}
        hasAddRemoveValues={hasAddRemoveValues}
        skipQuery={skipQuery}
        value={value}
        shouldConsiderFieldValue={shouldConsiderFieldValue}
        viewLabel={viewLabel}
        viewLabelLocale={viewLabelLocale}
        templateId={templateId}
        plannerElementSubType={plannerElementSubType}
        assessmentTools={assessmentTools}
        testId={`${testId}-${selectedFieldKey}`}
        fieldObjConfigOptions={fieldObjConfigOptions}
        showSearchFilter={showSearchFilter}
        showSideNavigation={showSideNavigation}
        showRadioButton={showRadioButton}
        showPopupForSubFields={showPopupForSubFields}
        getLearningGoalsTagCountTooltip={getLearningGoalsTagCountTooltip}
        shouldUpdateActiveQuestionOnClick={false}
        curriculumProgram={curriculumProgram}
        learningCourseId={learningCourseId}
      />
    );
  };

  // for "addLearningGoals" field, value will always be null as this component is just to show
  // label and dropdown in edit mode and only label in view mode
  shouldShowEmpty = () => false;

  renderEdit = () => {
    const { isModalVisible, selectedFieldKey } = this.state;
    const isLearningGoalsRevampField = getIsLearningGoalsRevampEnabled();

    // When learning goals revamp is enabled, render the new AssessmentLearningGoals component
    if (isLearningGoalsRevampField) {
      const {
        field_list,
        unitPlanFields,
        courseSubjects,
        updateInputField,
        assessmentFields,
        hasAddRemoveValues,
        assignmentSubjects,
        selectedLearningGoal,
        defaultOptions,
        unitPlanTemplate,
        getLearningGoalsTagCountTooltip,
        subFields,
        parentId,
        parentType,
      } = this.props;

      return isModalVisible ? (
        <AssessmentLearningGoals
          field_list={field_list}
          unitPlanFields={unitPlanFields}
          courseSubjects={courseSubjects}
          updateInputField={updateInputField}
          assessmentFields={assessmentFields}
          hasAddRemoveValues={hasAddRemoveValues}
          assignmentSubjects={assignmentSubjects}
          selectedLearningGoal={selectedLearningGoal}
          defaultOptions={defaultOptions}
          unitPlanTemplate={unitPlanTemplate}
          getLearningGoalsTagCountTooltip={getLearningGoalsTagCountTooltip}
          subFields={subFields}
          selectedLearningGoalKey={selectedFieldKey}
          onClose={this.onCloseLearningGoalsModal}
          assessmentId={parentType === ASSESSMENT ? parentId : null}
        />
      ) : null;
    }

    // Otherwise, render the old modal-based approach (which uses labelComponent dropdown from MiddlePane)
    return isModalVisible ? this.renderLearningGoalsModal() : null;
  };

  renderView = () => {
    const {
      assessmentId,
      defaultOptions,
      assessmentFields,
      unitPlanFields,
      field_list,
      userType,
      t,
      showExpandedLearningGoals = false,
      assessmentToolsEvaluationDetails,
      showLearningGoalsRating,
      curriculumProgramType,
    } = this.props;

    // For staff, learning goals in view mode are rendered through LearningGoalsNestedView and LearningGoalsListView components
    // So this should render learning goals only for students
    if (userType === "staff") {
      return null;
    }

    if (showLearningGoalsRating) {
      return (
        <div style={styles.ratingContainer}>
          <PypEvaluationV2
            {...assessmentToolsEvaluationDetails}
            curriculumProgramType={curriculumProgramType}
            isMinimalDesign={true}
            showCriteriaDescriptorButton={true}
            showAllElements={userType !== "staff"}
            evaluationContainerStyle={styles.evaluationContainerStyle}
            evaluationBodyStyle={styles.evaluationBodyStyle}
            criteriaSetHeaderRowStyle={styles.criteriaSetHeaderRowStyle}
            headerRowLeftContainerStyle={styles.headerRowLeftContainerStyle}
            customHeaderLabelStyle={styles.customHeaderLabelStyle}
            criteriaSetHeaderLabelPreviewStyle={
              styles.criteriaSetHeaderLabelPreviewStyle
            }
            showUnorderedList={true}
            hideArchivedSubjectStandards={true}
          />
        </div>
      );
    }

    if (!showExpandedLearningGoals) {
      return null;
    }

    const learningGoalFields = _.map(defaultOptions, "key");

    return (
      <>
        {_.map(learningGoalFields, fieldKey => {
          const isValueNotEmpty = checkEmptyElementValue({
            fieldKey,
            data: { allFields: assessmentFields },
            unitPlanFields,
            fieldObj: field_list[fieldKey],
            ignoreUnitPlanSubjectCheck: true,
            checkOnlyValueKeysForBenchmarks: true,
          });

          if (!isValueNotEmpty) {
            return null;
          }

          const fieldData = _.find(assessmentFields, { uid: fieldKey });
          const fieldObj = field_list[fieldKey];
          const {
            viewType,
            config: {
              viewLabel,
              label,
              styles: fieldStyles = {},
              fieldTypeConfig = {},
              plannerElementConfig = {},
            } = {},
          } = fieldObj;

          const assessmentFieldValue = _.get(fieldData, "value", "");
          const unitPlanFieldValue = _.get(
            _.find(unitPlanFields, { uid: fieldKey }),
            "value",
            ""
          );
          const resolvedValue =
            _.get(fieldData, "resolvedMinimalTree", "") || {};

          const value = getElementValue({
            fieldUID: fieldKey,
            valueKeys: assessmentFieldValue,
            unitPlanFieldValue,
            fieldObj,
            resolvedValue: resolvedValue,
            t,
          });

          const style = {
            ..._.get(fieldStyles, "style", {}),
            ..._.get(fieldStyles, "viewStyle", {}),
          };

          return (
            <div style={style} key={fieldKey}>
              <FieldComponent
                {...fieldTypeConfig}
                fieldId={_.get(fieldData, "id", "")}
                fieldKey={fieldKey}
                value={value}
                type={viewType}
                mode={"view"}
                parentType={"ASSESSMENT"}
                parentId={assessmentId}
                label={viewLabel || label}
                field_list={field_list}
                assessmentFields={assessmentFields}
                unitPlanFields={unitPlanFields}
                plannerElementConfig={plannerElementConfig}
                subFields={getSubFields({
                  fieldKey,
                  fieldObj: field_list[fieldKey],
                  unitPlanFields,
                })}
                labelComponent={
                  <LabelComponent
                    label={viewLabel || label}
                    labelStyle={styles.labelStyle}
                  />
                }
              />
            </div>
          );
        })}
      </>
    );
  };
}

AddLearningGoals.defaultProps = {
  ...UIBaseComponent.defaultProps,
  testId: "IBComponents-addLearningGoals",
};
const mapActionCreators = {
  updateAssessmentInRedux,
  updateAssessment,
};

const mapStateToProps = state => {
  return {
    userType: getUserType(state),
    curriculumProgramType: getCurrentCurriculumProgramType(state),
  };
};

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(mapStateToProps, mapActionCreators)
)(AddLearningGoals);
